/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { RefObject, JSX } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import type { TimeReducerAction } from './timeReducer';
import type { EditableTimeSegmentType } from './types';
import { isBackspaceOrDelete, isSelectAll } from '#utils/PRIVATE_keyboardUtils';
import { isNumberOnlyString } from '#utils/PRIVATE_dayUtils';
import { classNames } from '#utils/UNSAFE_classNames';
import { segmentStyles } from './themes/SegmentStyles.css';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import type { AmPmLocalizedValues } from '#utils/UNSAFE_timeUtils';
import { useDayPeriodInputHandlers } from './useDayPeriodInputHandlers';

type AriaProps = Pick<
  UnpackSignals<JSX.AriaAttributes>,
  'aria-label' | 'aria-valuemax' | 'aria-valuemin' | 'aria-valuenow' | 'aria-valuetext'
>;

type DayPeriodProps = {
  type: 'dayPeriod';
  /**
   * The am/pm localized values. This is used when the segment type is 'dayPeriod'
   * and is required.
   */
  amPm: AmPmLocalizedValues;
};

type NonDayPeriodProps = {
  type: Exclude<EditableTimeSegmentType, 'dayPeriod'>;
  amPm?: never;
};

type Props = AriaProps &
  (DayPeriodProps | NonDayPeriodProps) & {
    /** A ref that is provided for the first segment in a time field. */
    inputRef?: RefObject<HTMLDivElement>;

    /**
     * True if the segment is complete in the sense that it contains a complete valid value.
     * For instance, entering '0' and then '1-9' is considered a complete value for a minute since minute has 2-digits.
     * Also true if typing any additional digit would cause the segment value to exceed the max.
     */
    isComplete?: boolean;

    /**
     * True if the segment is disabled.
     */
    isDisabled?: boolean;

    /**
     * True if the field is in focused state.
     */
    isFieldFocused?: boolean;

    /**
     * True if the segment should be hidden.
     */
    isHidden?: boolean;

    /**
     * True if the entire time field is highlighted.
     */
    isHighlighted?: boolean;

    /**
     * True if the segment is invalid.
     */
    isInvalid?: boolean;

    /**
     * Specifies for accessibility purposes whether a value is required.
     */
    isRequired?: boolean;

    /**
     * True if the segment is selected.
     */
    isSelected?: boolean;

    /**
     * Placeholder displayed when the segment has no display value.
     */
    placeholder?: string;

    /**
     * The text to display for this segment.
     */
    text?: string;

    /**
     * The segment of a time which is being represented, i.e. the hour, minute, second, millisecond, dayPeriod.
     */
    type: EditableTimeSegmentType;

    /**
     * Callback invoked when a change to the segment is requested via user action, such as typing
     * a new value or pressing the Backspace or Delete keys.
     */
    onChange?: (action: TimeReducerAction) => void;
  };

// Used to disable browser cut and paste, which don't really work with segments (copy is fine).
const preventDefaultHandler = (e: Event) => {
  e.preventDefault();
};

const isIos = () => getClientHints().platform === 'ios';

/**
 * A TimeSegment renders a part of a time, e.g. the hour, minute, second, millisecond, dayPeriod.
 * It can be used as a spinbutton to step or spin its value.
 */
export const TimeSegment = ({
  amPm,
  'aria-label': ariaLabel,
  'aria-valuemax': ariaValueMax,
  'aria-valuemin': ariaValueMin,
  'aria-valuenow': ariaValueNow,
  'aria-valuetext': ariaValueText,
  inputRef,
  isDisabled,
  isFieldFocused,
  isHidden,
  isHighlighted,
  isInvalid,
  isRequired,
  isSelected,
  onChange,
  placeholder,
  text,
  type
}: Props) => {
  const hasDisplayValue = text && text.length > 0;
  const rootClasses = classNames([
    segmentStyles.base,
    isSelected && segmentStyles.selected,
    isHighlighted && segmentStyles.highlighted,
    isFieldFocused && segmentStyles.accessibleTouchTarget
  ]);
  const placeholderClasses = classNames([
    segmentStyles.placeholder,
    isHidden || hasDisplayValue ? segmentStyles.hidden : segmentStyles.notHidden
  ]);
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const emptySegmentValueText = translations.formControl_empty_segment();

  // If a ref was passed in, use it, otherwise get a ref.
  const sRef = useRef<HTMLDivElement>(null);
  const segmentRef = inputRef ?? sRef;

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        isDisabled === true ||
        event.key === 'Tab' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'Enter' ||
        // JET-73547: let Escape pass through in order to close a popup or dialog.
        event.key === 'Escape' ||
        isSelectAll(event)
      ) {
        // Let the event pass through.
        return;
      }
      if (isHighlighted) {
        // If time is selected, ignore edits and let backspace/delete pass through to clear.
        if (isBackspaceOrDelete(event) || event.key === 'Backspace') {
          return;
        }
      } else if (isNumberOnlyString(event.key)) {
        // do not send this for a dayPeriod segment since a dayPeriod segment does not have numbers.
        if (type !== 'dayPeriod') {
          onChange?.({
            actionType: 'updateSegment',
            data: { type, text: event.key }
          });
        }
      } else if (isBackspaceOrDelete(event) || event.key === 'Backspace') {
        // Android sends Backspace as event.key, not event.code, so check for it.
        onChange?.({ actionType: 'clearSegment', data: { type } });
      } else if (event.key === 'ArrowUp') {
        onChange?.({ actionType: 'step', data: { direction: 'increase', type } });
      } else if (event.key === 'ArrowDown') {
        onChange?.({ actionType: 'step', data: { direction: 'decrease', type } });
      } else if (event.key === 'PageUp') {
        onChange?.({ actionType: 'page', data: { direction: 'increase', type } });
      } else if (event.key === 'PageDown') {
        onChange?.({ actionType: 'page', data: { direction: 'decrease', type } });
      } else if (event.key === 'Home') {
        onChange?.({ actionType: 'goToHome', data: { type } });
      } else if (event.key === 'End') {
        onChange?.({ actionType: 'goToEnd', data: { type } });
      } else if (type === 'dayPeriod') {
        // return and don't call preventDefault so that we can use input handlers for dayPeriod
        // that we get from useDayPeriodInputHandlers.
        // keydown does not work for Android (event.key is 'Unspecified') or IME (like Chinese), and input handlers work for everything.
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    },
    [isDisabled, isHighlighted, onChange, type]
  );

  // Gets the input handlers that we need for the dayPeriod segment to allow typing letters to toggle.
  const dayPeriodHandler = useDayPeriodInputHandlers(
    amPm ?? { am: 'AM', pm: 'PM' },
    segmentRef,
    onChange
  );

  // Used to focus the segment when isSelected is true.
  useEffect(() => {
    if (isSelected) {
      segmentRef.current?.focus();
    }
  }, [isSelected, segmentRef, type]);

  // If ariaValueText is undefined, substitute a string that indicates the segment is empty.
  // If ariaValueNow and ariaValueText represent the same value, omit the value text; this
  // is the same as what we do in NumberInputText.
  const valueText =
    ariaValueText === undefined
      ? emptySegmentValueText
      : ariaValueNow?.toString() === ariaValueText
      ? undefined
      : ariaValueText;
  // This attribute is used to distinguish between segments when clicked (see TimeField.tsx).
  const customDataAttribute = { 'data-segment': [type] };
  return (
    // Introduce a div wrapper around the contentEditable element to prevent it from grabbing focus.
    // https://stackoverflow.com/questions/34354085/clicking-outside-a-contenteditable-div-stills-give-focus-to-it
    // JET-64874: We change the segment's role to "textbox" on iOS to work around a VoiceOver bug, and when we do that
    // we need to add aria-multiline={false}
    <div>
      <div
        ref={segmentRef}
        role={isIos() ? 'textbox' : 'spinbutton'}
        aria-disabled={isDisabled}
        aria-invalid={isInvalid || undefined}
        aria-label={ariaLabel}
        aria-multiline={isIos() ? false : undefined}
        aria-required={isRequired}
        aria-valuemin={isIos() ? undefined : ariaValueMin}
        aria-valuemax={isIos() ? undefined : ariaValueMax}
        aria-valuenow={isIos() ? undefined : ariaValueNow}
        aria-valuetext={isIos() ? undefined : valueText}
        autocapitalize="off"
        autocorrect="off"
        class={rootClasses}
        contentEditable={!isDisabled}
        {...(type === 'dayPeriod' ? dayPeriodHandler : {})}
        {...customDataAttribute}
        enterkeyhint={isDisabled ? undefined : 'done'}
        inputMode={isDisabled ? 'none' : type !== 'dayPeriod' ? 'numeric' : 'text'}
        onCut={preventDefaultHandler}
        onKeyDown={onKeyDown}
        onPaste={preventDefaultHandler}
        spellcheck={false}
        tabIndex={isSelected ? 0 : -1}>
        <span aria-hidden="true" class={placeholderClasses} {...customDataAttribute}>
          {placeholder}
        </span>
        {text}
      </div>
    </div>
  );
};
